import { Fragment, useEffect } from 'react'

import type { LinkProps } from '../link'
import type { ListItemProps } from './list.types'

import { styled, useStyleConfig, vui } from '../core'
import Link from '../link'
import { cs, filterUndefined, isReactText, isString } from '../utils'
import { useListContext } from './context'
import ListIcon from './listIcon'
import ListText from './listText'

/** Used as optional content wrapper when list item should be a link. */
const ListItemLink = vui<'a', LinkProps>((props, ref) => (
  <Link
    borderRadius="none"
    className="vui-listItemLink vui-linkNoUnderline"
    focusRing={0}
    h="100%"
    px={2}
    ref={ref}
    variant="plain"
    w="100%"
    {...props}
  />
))

export const ListItemBase = styled.liBox`
  display: flex;
  line-height: normal;
  outline: none;
  transition-duration: fast;

  &[aria-disabled='true'] {
    cursor: not-allowed;
    user-select: none;
    opacity: 0.5;
  }

  &:focus-visible {
    z-index: 1;
  }
`

/**
 * Displays a list item with text and optional icons. Can be shown as interactive or selected.
 * Optionally wraps the content with an <a> element when rendering a link.
 */
export const ListItem = vui<'li', ListItemProps>((props, ref) => {
  const mergedProps = { ...useListContext(), ...props }
  const {
    children,
    center,
    centerH,
    centerV = true,
    className,
    column,
    disabled,
    startIcon,
    endIcon,
    iconLeft,
    iconRight,
    isInteractive: isInteractiveProp,
    isSelected,
    isTruncated,
    itemLeft,
    itemRight,
    linkProps,
    onClick,
    text,
    ...rest
  } = mergedProps

  const resolvedStartIcon = startIcon ?? iconLeft
  const resolvedEndIcon = endIcon ?? iconRight
  const styles = useStyleConfig('List', useListContext())
  const { activeBg, hoverBg, selectedBg, ...itemStyles } = styles.item

  const isLinkItem = linkProps !== undefined
  const isInteractive = isInteractiveProp || isLinkItem || onClick !== undefined
  const alignItems = (center || (column ? centerH : centerV)) && 'center'
  const flexDirection = column && 'column'
  const justifyContent = (center || (column ? centerV : centerH)) && 'center'

  const ContentWrapper = isLinkItem ? ListItemLink : Fragment
  const contentWrapperProps = isLinkItem ? { alignItems: 'center', display: 'inline-flex' } : {}

  const interactiveProps =
    !disabled && isInteractive
      ? {
          cursor: 'pointer',
          ...(isLinkItem
            ? { focusWithinRing: 2, focusWithinRingInset: true }
            : { focusVisibleRing: 2, focusVisibleRingInset: true }),
          hoverBg,
          activeBg,
          tabIndex: !isLinkItem ? 0 : undefined,
          userSelect: 'none',
        }
      : {}

  const aliasedProps = filterUndefined({
    alignItems,
    bg: isSelected ? selectedBg : undefined,
    'aria-disabled': disabled,
    flexDirection,
    justifyContent,
    px: isLinkItem ? 0 : undefined,
  })

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (iconLeft !== undefined) {
        console.warn('[List.Item] `iconLeft` is deprecated. Use `startIcon` instead.')
      }
      if (iconRight !== undefined) {
        console.warn('[List.Item] `iconRight` is deprecated. Use `endIcon` instead.')
      }
    }
  }, [iconLeft, iconRight])

  return (
    <ListItemBase
      bg={disabled ? rest.disabledBg || 'sandstone.95' : undefined}
      className={cs('vui-listItem', className)}
      onClick={!disabled ? onClick : undefined}
      ref={ref}
      {...itemStyles}
      {...interactiveProps}
      {...aliasedProps}
      {...rest}
    >
      <ContentWrapper {...linkProps} {...contentWrapperProps}>
        {itemLeft}

        {isString(resolvedStartIcon) ? (
          <ListIcon mr={1} name={resolvedStartIcon} verticalAlign={'middle'} />
        ) : (
          resolvedStartIcon
        )}

        {children ?? (isReactText(text) ? <ListText {...{ isTruncated, text }} /> : text)}

        {isString(resolvedEndIcon) ? (
          <ListIcon ml="auto" name={resolvedEndIcon} verticalAlign={'middle'} />
        ) : (
          resolvedEndIcon
        )}

        {itemRight}
      </ContentWrapper>
    </ListItemBase>
  )
})

ListItem.displayName = 'ListItem'
export default ListItem
